import { useEffect, useMemo, useRef, useState } from "react";
import { Form, useFormikContext } from "formik";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { useDraggableFields } from "@vega-core/components/Form/Controls/Input/SearchMultiSelect/hooks/useDraggableFields";
import {
  getUserOrFreeformEmailItems,
  type SearchMultiSelectItemWithUser,
  SearchUserMultiselect,
} from "@vega-core/components/Form/Controls/Input/SearchUser/SearchUserMultiselect";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { TextareaField } from "@vega-core/components/Form/Fields/TextareaField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { m } from "@vega-core/paraglide/messages";

type FormValues = {
  emailSubject: string;
  emailText: string;
  to: SearchMultiSelectItemWithUser[];
  cc: SearchMultiSelectItemWithUser[];
  sender: SearchMultiSelectItemWithUser | null;
};

type EmailContentStepProps = {
  closeModal: () => void;
  dealLink: string;
  firstProspectLpsUsers: SearchMultiSelectItemWithUser[];
  gpUsers: SearchMultiSelectItemWithUser[];
  isMultipleProspects: boolean;
  isSendEmailPending: boolean;
  hasAnyLockedProspects: boolean;
  isSenderSearchLoading?: boolean;
  onNext: () => void;
  onSenderSearchChange?: (value: string) => void;
  senderSearchValue?: string;
};

export const EmailContentStep = ({
  closeModal,
  dealLink,
  firstProspectLpsUsers,
  gpUsers,
  isMultipleProspects,
  isSendEmailPending,
  hasAnyLockedProspects,
  isSenderSearchLoading,
  onNext,
  onSenderSearchChange,
  senderSearchValue,
}: EmailContentStepProps) => {
  const { submitForm, values, errors, setFieldValue, setValues } =
    useFormikContext<FormValues>();

  const emailTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const [cursorPos, setCursorPos] = useState<number | null>(null);
  const [ccSearchValue, setCcSearchValue] = useState("");

  useEffect(() => {
    if (cursorPos !== null && emailTextAreaRef.current) {
      const el = emailTextAreaRef.current;
      el.focus();
      el.selectionStart = cursorPos;
      el.selectionEnd = cursorPos;
      setCursorPos(null);
    }
  }, [cursorPos]);

  const { handleSelectionChange } = useDraggableFields({
    fieldValues: { to: values.to, cc: values.cc },
    setFieldValues: (updatedFields: {
      to: SearchMultiSelectItemWithUser[];
      cc: SearchMultiSelectItemWithUser[];
    }) => {
      setValues({ ...values, ...updatedFields });
    },
  });

  // When search is cleared after selection, gpUsers resets to [relationship] only.
  // Keep the selected sender in the list so the Combobox can display it.
  const senderItems = useMemo(() => {
    if (!values.sender) return gpUsers;

    if (gpUsers.some(({ id }) => id === values.sender?.id)) return gpUsers;

    return [values.sender, ...gpUsers];
  }, [gpUsers, values.sender]);

  const handleAddDealLink = (currentValue: string) => {
    if (!emailTextAreaRef.current) return;

    const cursor = emailTextAreaRef.current.selectionEnd;
    const newValue =
      currentValue.slice(0, cursor) + dealLink + currentValue.slice(cursor);
    setFieldValue("emailText", newValue);
    setCursorPos(cursor + dealLink.length);
  };

  const handleSubmitClick = () => {
    if (hasAnyLockedProspects) {
      onNext();
    } else {
      submitForm();
    }
  };

  const mainActionText = hasAnyLockedProspects ? m.btn_next() : m.btn_send();

  const ccItems = getUserOrFreeformEmailItems({
    users: firstProspectLpsUsers,
    selectedItems: values.cc,
    searchValue: ccSearchValue,
  });

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        disabled: isSendEmailPending,
        mainAction: handleSubmitClick,
        mainActionText,
        secondaryAction: closeModal,
        secondaryActionText: m.btn_cancel(),
        thirdActionElement: isMultipleProspects ? undefined : (
          <ThemedButton
            dataTestId="add-deal-link-button"
            onClick={() => handleAddDealLink(values.emailText)}
            type="button"
            variant="secondary"
          >
            {m.coInvestments_editEmail_addDealLink_button()}
          </ThemedButton>
        ),
      }}
      dataTestId="email-edit-modal-email-content-step"
      description={m.coInvestments_editEmail_description()}
      fluidHeight
      open
      setOpen={closeModal}
      title={m.coInvestments_editEmail_title()}
    >
      <Form>
        <div className="flex flex-col gap-4 items-stretch text-start mx-auto">
          {isMultipleProspects ? null : (
            <>
              <SearchUserMultiselect
                draggable
                error={errors.to as string}
                items={firstProspectLpsUsers}
                leftLabel={m.coInvestments_editEmail_emailSentTo_label()}
                multiple
                name="to"
                onSelectionChange={(newUsers) =>
                  handleSelectionChange("to", newUsers)
                }
                selectedValues={values.to}
              />
              <SearchUserMultiselect
                autoHighlight
                draggable
                items={ccItems}
                leftLabel={m.coInvestments_editEmail_cc_label()}
                multiple
                name="cc"
                onSearchChange={setCcSearchValue}
                onSelectionChange={(newUsers) => {
                  handleSelectionChange("cc", newUsers);
                  setCcSearchValue("");
                }}
                selectedValues={values.cc}
              />
              <SearchUserMultiselect
                error={errors.sender}
                hint={m.coInvestments_editEmail_bcc_notice()}
                isLoading={isSenderSearchLoading}
                items={senderItems}
                leftLabel={m.coInvestments_editEmail_emailSentFrom_label()}
                multiple={false}
                name="sender"
                onSearchChange={onSenderSearchChange}
                onSelectionChange={(newSender) => {
                  setFieldValue("sender", newSender);
                  onSenderSearchChange?.("");
                }}
                searchInputValue={senderSearchValue?.trim() || undefined}
                selectedValues={values.sender}
              />
            </>
          )}

          <InputField
            disabled={isMultipleProspects}
            leftLabel={m.coInvestments_editEmail_subjectLabel()}
            name="emailSubject"
            placeholder={m.coInvestments_editEmail_subjectPlaceholder()}
            withBorder
          />

          <TextareaField
            dataTestId="email-text-textarea"
            disabled={isMultipleProspects}
            name="emailText"
            placeholder={m.coInvestments_editEmail_placeholder()}
            ref={emailTextAreaRef}
            resize={false}
            rows={12}
            withBorder
          />
        </div>
      </Form>
    </ActionaryModal>
  );
};
